// contexts/NotificationContext.tsx
import React, { createContext, useContext, useCallback, useState, ReactNode } from 'react';
import { useWebSocket } from '../hooks/useWebSocket'; // your existing hook

export interface Notification {
    id?: string;
    type: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';
    title: string;
    message: string;
    userId?: string;
    timestamp?: string;
}

interface NotificationContextType {
    notifications: Notification[];
    addNotification: (notification: Notification) => void;
    removeNotification: (id?: string) => void;
    clearNotifications: () => void;
    isConnected: boolean;
    unreadCount: number;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const { isConnected } = useWebSocket({
        url: process.env.REACT_APP_WEB_SOCKET_URL || 'http://localhost:8081/websocket',
        onConnect: () => console.log('✅ WebSocket connected'),
        onDisconnect: () => console.log('❌ WebSocket disconnected'),
        onMessage: (message) => {
            // Add incoming notification to state
            addNotification(message);
            console.log('🔔 New notification received:', message);
        },
    });

    const addNotification = useCallback((notification: Notification) => {
        setNotifications(prev => {
            // Avoid duplicates by id if provided
            if (notification.id && prev.some(n => n.id === notification.id)) {
                return prev;
            }
            return [{ ...notification, timestamp: notification.timestamp || new Date().toISOString() }, ...prev].slice(0, 50); // keep last 50
        });
    }, []);

    const removeNotification = useCallback((id?: string) => {
        if (!id) return;
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    const clearNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    const unreadCount = notifications.length; // customize logic if you track "read" status

    return (
        <NotificationContext.Provider value={{
            notifications,
            addNotification,
            removeNotification,
            clearNotifications,
            isConnected,
            unreadCount,
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within NotificationProvider');
    }
    return context;
};